import React from 'react';
import { 
  Brain, 
  Cloud, 
  BarChart3, 
  Cog, 
  ShieldCheck, 
  UserCheck, 
  Code2, 
  Database, 
  Layers, 
  Terminal,
  ArrowRight,
  Building2,
  Users2,
  Sparkles
} from 'lucide-react';
import Banner from "../../assets/Banner4.png";

export default function MobileHeroSection() {
  // Enhanced Feature cards data with unique highlighted CRM and ERP entries
  const featureCards = [
    { title: 'CRM', subtitle: 'Platforms', icon: <Users2 className="w-5 h-5 text-white" />, highlight: true },
    { title: 'ERP', subtitle: 'Systems', icon: <Building2 className="w-5 h-5 text-white" />, highlight: true },
    { title: 'AI & ML', subtitle: 'Solutions', icon: <Brain className="w-5 h-5 text-[#f25c05]" /> },
    { title: 'Cloud', subtitle: 'Services', icon: <Cloud className="w-5 h-5 text-[#f25c05]" /> },
    { title: 'Data', subtitle: 'Analytics', icon: <BarChart3 className="w-5 h-5 text-[#f25c05]" /> },
    { title: 'Automation', subtitle: '& Integration', icon: <Cog className="w-5 h-5 text-[#f25c05]" /> },
    { title: 'Cyber', subtitle: 'Security', icon: <ShieldCheck className="w-5 h-5 text-[#f25c05]" /> },
    { title: 'Consulting', subtitle: 'Services', icon: <UserCheck className="w-5 h-5 text-[#f25c05]" /> },
  ];

  // Tech stack pills with CRM & ERP included
  const techPills = [
    { label: 'CRM Solutions', icon: <Users2 className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'ERP Systems', icon: <Building2 className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'MERN Stack', icon: <Database className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'MEAN Stack', icon: <Layers className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'AI Integration', icon: <Brain className="w-3.5 h-3.5 text-[#f25c05]" /> },
    { label: 'Full-Stack Web', icon: <Terminal className="w-3.5 h-3.5 text-[#f25c05]" /> },
  ];

  return (
    <section className="block lg:hidden w-full bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#e2e8f0] px-4 py-8 sm:px-6 sm:py-12 font-sans text-slate-800 relative overflow-hidden">
      
      {/* Background Ambient Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-xl mx-auto space-y-6 relative z-10">
        
        {/* Tagline Badge */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100/90 border border-orange-200 text-xs font-black tracking-wider text-[#f25c05] uppercase shadow-xs">
          <Sparkles className="w-3.5 h-3.5 text-[#f25c05]" />
          <span>INNOVATE. INTEGRATE. ELEVATE.</span>
        </div>

        {/* Main Headline */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tight">
          Enterprise CRM, ERP & <span className="text-[#f25c05]">AI Solutions.</span>
          <br />
          <span className="font-extrabold text-slate-800">Real Business Impact.</span>
        </h1>

        {/* Description Paragraph */}
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-medium">
          Inimai Solutions empowers growing enterprises with modern software engineering—specializing in custom <strong className="font-bold text-slate-900">CRM & ERP systems</strong>, high-performance <strong className="font-bold text-slate-900">MERN & MEAN stack apps</strong>, and intelligent <strong className="font-bold text-slate-900">AI workflow integration</strong>.
        </p>

        {/* Tech Stack Pills */}
        <div className="flex flex-wrap gap-2 pt-1">
          {techPills.map((pill, idx) => (
            <div 
              key={idx} 
              className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-orange-200/80 px-2.5 py-1 rounded-lg shadow-xs text-xs font-semibold text-slate-700"
            >
              {pill.icon}
              <span>{pill.label}</span>
            </div>
          ))}
        </div>

        {/* Call-to-Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <a
            href="/services"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#f25c05] active:bg-[#d95000] text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-md shadow-[#f25c05]/20 transition-transform active:scale-95"
          >
            <span>Explore Services</span>
            <ArrowRight className="w-4 h-4" />
          </a>

          <a
            href="/about"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white active:bg-slate-50 text-slate-800 border-2 border-orange-200 font-semibold text-sm px-6 py-3 rounded-xl shadow-xs transition-transform active:scale-95"
          >
            <span>About Us</span>
          </a>
        </div>

        {/* Banner Image Container */}
        <div className="pt-4 flex justify-center">
          <div className="relative w-full max-w-sm sm:max-w-md bg-white/60 backdrop-blur-sm p-3 rounded-2xl border border-white/80 shadow-xl shadow-slate-200/60">
            <img 
              src={Banner} 
              alt="Inimai Solutions Banner" 
              className="w-full h-auto object-contain rounded-xl max-h-64 sm:max-h-72" 
            />
          </div>
        </div>

        {/* Features Card Grid (Distinct 2-column layout highlighting CRM & ERP) */}
        <div className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              Core Capabilities
            </h2>
            <span className="text-[10px] font-bold text-[#f25c05] bg-orange-100/80 px-2 py-0.5 rounded-full">
              Enterprise Ready
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {featureCards.map((card, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-2.5 p-3 rounded-xl border shadow-xs transition-all active:scale-95 ${
                  card.highlight 
                    ? 'bg-gradient-to-r from-[#f25c05] to-orange-500 border-orange-400 text-white shadow-md shadow-orange-500/20' 
                    : 'bg-white/90 backdrop-blur-sm border-slate-200/80 text-slate-800'
                }`}
              >
                <div className={`p-2 rounded-lg shrink-0 flex items-center justify-center ${
                  card.highlight 
                    ? 'bg-white/20 backdrop-blur-md' 
                    : 'bg-orange-50 border border-orange-200/60'
                }`}>
                  {card.icon}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-black leading-tight truncate ${card.highlight ? 'text-white' : 'text-slate-800'}`}>
                    {card.title}
                  </p>
                  <p className={`text-[10px] font-medium truncate ${card.highlight ? 'text-orange-100' : 'text-slate-500'}`}>
                    {card.subtitle}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}